import "@/App.css";
import "@/index.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth, isAdminRole, isSuperAdmin } from "@/lib/auth";
import HeadMeta, { BrandThemeVars } from "@/components/common/HeadMeta";
import ForcePasswordChange from "@/components/auth/ForcePasswordChange";

import PublicLayout from "@/components/layout/PublicLayout";
import PortalLayout from "@/components/layout/PortalLayout";

// Public/marketing pages ship eager — they render on first paint.
import Home from "@/pages/public/Home";
import Pricing from "@/pages/public/Pricing";
import Locations from "@/pages/public/Locations";
import Features from "@/pages/public/Features";
import About from "@/pages/public/About";
import FAQ from "@/pages/public/FAQ";
import KnowledgeBase from "@/pages/public/KnowledgeBase";
import Contact from "@/pages/public/Contact";
import Status from "@/pages/public/Status";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Cookies from "@/pages/public/Cookies";
import AcceptableUse from "@/pages/public/AcceptableUse";
import SLA from "@/pages/public/SLA";
import Enterprise from "@/pages/public/Enterprise";
import Partners from "@/pages/public/Partners";

// Auth pages are eager — first paint of the login form matters.
import Login from "@/pages/auth/Login";
import AdminLogin from "@/pages/auth/AdminLogin";
import SuperAdminLogin from "@/pages/auth/SuperAdminLogin";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import Verify from "@/pages/auth/Verify";
import AuthCallback from "@/pages/auth/AuthCallback";

// -------- Lazy-loaded portal pages (client-only surfaces, code-split) --------
const ClientDashboard      = lazy(() => import("@/pages/client/Dashboard"));
const MyVPS                = lazy(() => import("@/pages/client/MyVPS"));
const DeployVPS            = lazy(() => import("@/pages/client/DeployVPS"));
const ClientOrders         = lazy(() => import("@/pages/client/Orders"));
const ClientInvoices       = lazy(() => import("@/pages/client/Invoices"));
const ClientTransactions   = lazy(() => import("@/pages/client/Transactions"));
const ClientWallet         = lazy(() => import("@/pages/client/Wallet"));
const ClientSupport        = lazy(() => import("@/pages/client/Support"));
const ClientNotifications  = lazy(() => import("@/pages/client/Notifications"));
const ClientProfile        = lazy(() => import("@/pages/client/Profile"));
const ClientSecurity       = lazy(() => import("@/pages/client/Security"));
const ClientSettings       = lazy(() => import("@/pages/client/Settings"));
const ClientApiKeys        = lazy(() => import("@/pages/client/ApiKeys"));
const ClientActivity       = lazy(() => import("@/pages/client/ActivityLogs"));

// -------- Lazy-loaded admin pages (huge, gated behind admin role) -----------
const AdminDashboard       = lazy(() => import("@/pages/admin/Dashboard"));
const AdminCustomers       = lazy(() => import("@/pages/admin/Customers"));
const AdminRoles           = lazy(() => import("@/pages/admin/Roles"));
const AdminServers         = lazy(() => import("@/pages/admin/Servers"));
const AdminNodes           = lazy(() => import("@/pages/admin/Nodes"));
const AdminIPPools         = lazy(() => import("@/pages/admin/IPPools"));
const AdminTemplates       = lazy(() => import("@/pages/admin/Templates"));
const AdminPlans           = lazy(() => import("@/pages/admin/Plans"));
const AdminOrders          = lazy(() => import("@/pages/admin/Orders"));
const AdminInvoices        = lazy(() => import("@/pages/admin/Invoices"));
const AdminPayments        = lazy(() => import("@/pages/admin/Payments"));
const AdminTransactions    = lazy(() => import("@/pages/admin/Transactions"));
const AdminWallet          = lazy(() => import("@/pages/admin/WalletManagement"));
const AdminCoupons         = lazy(() => import("@/pages/admin/Coupons"));
const AdminTickets         = lazy(() => import("@/pages/admin/Tickets"));
const AdminAnnouncements   = lazy(() => import("@/pages/admin/Announcements"));
const AdminSystemLogs      = lazy(() => import("@/pages/admin/SystemLogs"));
const AdminAuditLogs       = lazy(() => import("@/pages/admin/AuditLogs"));
const AdminMonitoring      = lazy(() => import("@/pages/admin/Monitoring"));
const AdminSettings        = lazy(() => import("@/pages/admin/Settings"));
const AdminIntegrations    = lazy(() => import("@/pages/admin/Integrations"));
const AdminEmailTemplates  = lazy(() => import("@/pages/admin/EmailTemplates"));
const ClientProfileForAdmin = lazy(() => import("@/pages/client/Profile"));
const InfraLayout          = lazy(() => import("@/pages/admin/infra/InfraLayout"));
const InfraDashboard       = lazy(() => import("@/pages/admin/infra/Dashboard"));
const InfraClusters        = lazy(() => import("@/pages/admin/infra/Clusters"));
const InfraNodes           = lazy(() => import("@/pages/admin/infra/Nodes"));
const InfraStorage         = lazy(() => import("@/pages/admin/infra/Storage"));
const InfraNetworks        = lazy(() => import("@/pages/admin/infra/Networks"));
const InfraResourceUsage   = lazy(() => import("@/pages/admin/infra/ResourceUsage"));
const InfraHealth          = lazy(() => import("@/pages/admin/infra/Health"));
const CatalogOS            = lazy(() => import("@/pages/admin/infra/OperatingSystems"));
const CatalogRegions       = lazy(() => import("@/pages/admin/infra/Regions"));
const CatalogAZs           = lazy(() => import("@/pages/admin/infra/AvailabilityZones"));
const CatalogTemplates     = lazy(() => import("@/pages/admin/infra/Templates"));
const CatalogISOLibrary    = lazy(() => import("@/pages/admin/infra/IsoLibrary"));
const CatalogImages        = lazy(() => import("@/pages/admin/infra/ImageCatalog"));
const CatalogIPPools       = lazy(() => import("@/pages/admin/infra/IpPools"));
const InfraAudit           = lazy(() => import("@/pages/admin/infra/Audit"));
const AdminsRBAC           = lazy(() => import("@/pages/admin/AdminsRBAC"));

// Phase 0.5 — Configuration Center
const ConfigLayout         = lazy(() => import("@/pages/admin/system/ConfigLayout"));
const ConfigOverview       = lazy(() => import("@/pages/admin/system/Overview"));
const ConfigFeatureFlags   = lazy(() => import("@/pages/admin/system/FeatureFlags"));
const ConfigEnvironment    = lazy(() => import("@/pages/admin/system/EnvironmentManager"));

// ComingSoonScreen is exported as a named export from InfraLayout — keep it lazy too.
const ComingSoonScreen = lazy(() =>
  import("@/pages/admin/infra/InfraLayout").then((m) => ({ default: m.ComingSoonScreen })),
);

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-muted-foreground text-sm animate-pulse">Loading…</div>
  </div>
);

function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground text-sm">Authenticating…</div>
      </div>
    );
  }
  if (!user) return <Navigate to={requireSuperAdmin ? "/super-admin/login" : requireAdmin ? "/admin/login" : "/login"} replace />;
  if (requireSuperAdmin && !isSuperAdmin(user.role))     return <Navigate to="/admin/login" replace />;
  if (requireAdmin      && !isAdminRole(user.role))      return <Navigate to="/portal" replace />;
  // Clients must never see admin routes even if the guard is off elsewhere
  if (!requireAdmin && !requireSuperAdmin && isAdminRole(user.role) && window.location.pathname.startsWith("/admin")) {
    return <Navigate to="/portal" replace />;
  }
  // Force password change gate — blocks every protected page until cleared.
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function LockDark() {
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/"          element={<Home />} />
        <Route path="/pricing"   element={<Pricing />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/features"  element={<Features />} />
        <Route path="/about"     element={<About />} />
        <Route path="/faq"       element={<FAQ />} />
        <Route path="/kb"        element={<KnowledgeBase />} />
        <Route path="/contact"   element={<Contact />} />
        <Route path="/status"          element={<Status />} />
        <Route path="/enterprise"      element={<Enterprise />} />
        <Route path="/partners"        element={<Partners />} />
        <Route path="/privacy"         element={<Privacy />} />
        <Route path="/terms"           element={<Terms />} />
        <Route path="/cookies"         element={<Cookies />} />
        <Route path="/acceptable-use"  element={<AcceptableUse />} />
        <Route path="/sla"             element={<SLA />} />
      </Route>

      <Route path="/login"           element={<Login />} />
      <Route path="/admin/login"       element={<AdminLogin />} />
      <Route path="/super-admin/login" element={<SuperAdminLogin />} />
      <Route path="/register"        element={<Register />} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password"  element={<Reset />} />
      <Route path="/verify-email"    element={<Verify />} />

      <Route path="/portal" element={<ProtectedRoute><PortalLayout variant="client" /></ProtectedRoute>}>
        <Route index               element={<ClientDashboard />} />
        <Route path="vps"          element={<MyVPS />} />
        <Route path="deploy"       element={<DeployVPS />} />
        <Route path="orders"       element={<ClientOrders />} />
        <Route path="invoices"     element={<ClientInvoices />} />
        <Route path="transactions" element={<ClientTransactions />} />
        <Route path="wallet"       element={<ClientWallet />} />
        <Route path="support"      element={<ClientSupport />} />
        <Route path="notifications" element={<ClientNotifications />} />
        <Route path="profile"      element={<ClientProfile />} />
        <Route path="security"     element={<ClientSecurity />} />
        <Route path="settings"     element={<ClientSettings />} />
        <Route path="api-keys"     element={<ClientApiKeys />} />
        <Route path="activity"     element={<ClientActivity />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute requireAdmin><PortalLayout variant="admin" /></ProtectedRoute>}>
        <Route index                element={<AdminDashboard />} />
        <Route path="customers"     element={<AdminCustomers />} />
        <Route path="roles"         element={<AdminRoles />} />
        <Route path="rbac"          element={<ProtectedRoute requireSuperAdmin><AdminsRBAC /></ProtectedRoute>} />
        <Route path="servers"       element={<AdminServers />} />
        <Route path="nodes"         element={<AdminNodes />} />
        <Route path="ippools"       element={<AdminIPPools />} />
        <Route path="templates"     element={<AdminTemplates />} />
        <Route path="plans"         element={<AdminPlans />} />
        <Route path="orders"        element={<AdminOrders />} />
        <Route path="invoices"      element={<AdminInvoices />} />
        <Route path="payments"      element={<AdminPayments />} />
        <Route path="transactions"  element={<AdminTransactions />} />
        <Route path="wallet"        element={<AdminWallet />} />
        <Route path="coupons"       element={<AdminCoupons />} />
        <Route path="tickets"       element={<AdminTickets />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="system-logs"   element={<AdminSystemLogs />} />
        <Route path="audit-logs"    element={<AdminAuditLogs />} />
        <Route path="monitoring"    element={<AdminMonitoring />} />
        <Route path="integrations"  element={<AdminIntegrations />} />
        <Route path="email"         element={<AdminEmailTemplates />} />

        {/* Phase 5A + 5B + 5C — Infrastructure Control Center */}
        <Route path="infrastructure" element={<InfraLayout />}>
          <Route index                element={<InfraDashboard />} />
          <Route path="clusters"      element={<InfraClusters />} />
          <Route path="nodes"         element={<InfraNodes />} />
          <Route path="storage"       element={<InfraStorage />} />
          <Route path="networks"      element={<InfraNetworks />} />
          <Route path="resources"     element={<InfraResourceUsage />} />
          <Route path="health"        element={<InfraHealth />} />
          <Route path="audit"         element={<InfraAudit />} />
          <Route path="os"            element={<CatalogOS />} />
          <Route path="regions"       element={<CatalogRegions />} />
          <Route path="azs"           element={<CatalogAZs />} />
          <Route path="templates"     element={<CatalogTemplates />} />
          <Route path="iso"           element={<CatalogISOLibrary />} />
          <Route path="images"        element={<CatalogImages />} />
          <Route path="ippools"       element={<CatalogIPPools />} />
          <Route path="dns"           element={<ComingSoonScreen title="DNS Zones" phase="6" />} />
          <Route path="vlans"         element={<ComingSoonScreen title="VLANs" phase="6" />} />
          <Route path="pools"         element={<ComingSoonScreen title="Resource Pools" phase="6" />} />
          <Route path="queue"         element={<ComingSoonScreen title="Provision Queue" phase="6" />} />
          <Route path="jobs"          element={<ComingSoonScreen title="Jobs & Tasks" phase="6" />} />
          <Route path="settings"      element={<ComingSoonScreen title="Infrastructure Settings" phase="6" />} />
        </Route>

        <Route path="profile"       element={<ClientProfileForAdmin />} />

        {/* Phase 0.5 — Configuration Center. Reuses the existing
             Integrations / Settings / AuditLogs / Monitoring pages under a
             consolidated left-nav so operators discover them in one place. */}
        <Route path="system" element={<ConfigLayout />}>
          <Route index                element={<ConfigOverview />} />
          <Route path="providers"     element={<AdminIntegrations />} />
          <Route path="secrets"       element={<AdminIntegrations />} />
          <Route path="environment"   element={<ConfigEnvironment />} />
          <Route path="feature-flags" element={<ConfigFeatureFlags />} />
          <Route path="health"        element={<AdminMonitoring />} />
          <Route path="global"        element={<AdminSettings />} />
          <Route path="audit"         element={<AdminAuditLogs />} />
        </Route>
        <Route path="settings"      element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LockDark />
        <BrandThemeVars />
        <HeadMeta />
        <AppRouter />
        <Toaster position="top-right" richColors theme="dark" closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
